import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  FolderIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  CloudArrowUpIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/apiService";
import { GoogleDriveAttachment, UserRole } from "../../types";
import toast from "../../utils/toast";
import { useAuth } from "../../contexts/AuthContext";
import UploadFilesToProjectModal from "../workflow/UploadFilesToProjectModal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { useTranslation } from "react-i18next";

interface ProjectFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowInstanceId: number | null;
  projectName: string;
  projectMemberIds?: number[] | null;
  projectManagerId?: number | string | null;
  templateWorkflowId?: number | null;
}

type FileSortField =
  | "name"
  | "description"
  | "uploadedAt"
  | "uploadedByName"
  | "type";

type ColumnFilters = {
  name: string;
  description: string;
  uploadedAt: string;
  uploadedByName: string;
  type: string;
};

const INITIAL_COLUMN_FILTERS: ColumnFilters = {
  name: "",
  description: "",
  uploadedAt: "",
  uploadedByName: "",
  type: "",
};

const normalizeSearchText = (value: string): string =>
  value.toLocaleLowerCase("tr-TR");

const PROTECTED_FINANCE_FOLDER_NAMES = new Set(
  ["Faturalar", "İş Tutarı - Teklifler", "Tahsilatlar"].map((name) =>
    normalizeSearchText(name)
  )
);

const isFolder = (file: GoogleDriveAttachment): boolean =>
  file.mimeType === "application/vnd.google-apps.folder";

const isProtectedFinanceFolder = (file: GoogleDriveAttachment): boolean =>
  isFolder(file) &&
  Boolean(file.name) &&
  PROTECTED_FINANCE_FOLDER_NAMES.has(normalizeSearchText(file.name));

const getTypeLabel = (file: GoogleDriveAttachment): string =>
  isFolder(file) ? "Klasör" : "Dosya";

const FileNameTypeCell: React.FC<{ file: GoogleDriveAttachment }> = ({ file }) => {
  const { t } = useTranslation("projects");
  const nameRef = useRef<HTMLParagraphElement | null>(null);
  const typeRef = useRef<HTMLParagraphElement | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const fileTypeLabel = file.mimeType
    ? file.mimeType.split("/")[1] || file.mimeType
    : "";

  useEffect(() => {
    const nameNode = nameRef.current;
    const typeNode = typeRef.current;

    const updateOverflowState = () => {
      const nameOverflow = Boolean(
        nameNode &&
          (nameNode.scrollWidth > nameNode.clientWidth ||
            nameNode.scrollHeight > nameNode.clientHeight)
      );
      const typeOverflow = Boolean(
        typeNode &&
          (typeNode.scrollWidth > typeNode.clientWidth ||
            typeNode.scrollHeight > typeNode.clientHeight)
      );

      setShowInfo(nameOverflow || typeOverflow);
    };

    updateOverflowState();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateOverflowState());
      if (nameNode) observer.observe(nameNode);
      if (typeNode) observer.observe(typeNode);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateOverflowState);
    return () => window.removeEventListener("resize", updateOverflowState);
  }, [file.name, fileTypeLabel]);

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-start gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <p
            ref={nameRef}
            className="truncate font-medium text-gray-900 text-sm"
            title={!showInfo ? file.name : undefined}
          >
            {file.name}
          </p>
          {file.mimeType && (
            <p
              ref={typeRef}
              className="mt-0.5 truncate text-xs text-gray-500"
              title={!showInfo ? fileTypeLabel : undefined}
            >
              {fileTypeLabel}
            </p>
          )}
        </div>
        {showInfo && (
          <div className="relative flex-shrink-0 group/info">
            <InformationCircleIcon className="mt-0.5 w-4 h-4 text-sky-500 cursor-help" />
            <div className="pointer-events-none invisible opacity-0 group-hover/info:visible group-hover/info:opacity-100 transition-opacity absolute left-full top-0 z-[80] ml-2 w-80 max-w-[32rem] rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-2xl">
              <div className="space-y-1">
                <div>
                  <span className="font-semibold text-slate-200">
                    {isFolder(file)
                      ? t("projectFilesModal.folderName")
                      : t("projectFilesModal.fileName")}:
                  </span>{" "}
                  <span className="whitespace-pre-wrap break-words">{file.name}</span>
                </div>
                {file.mimeType && (
                  <div>
                    <span className="font-semibold text-slate-200">
                      {t("projectFilesModal.fileType")}:
                    </span>{" "}
                    <span className="whitespace-pre-wrap break-words">
                      {file.mimeType}
                    </span>
                  </div>
                )}
              </div>
              <div className="absolute right-full top-3 h-0 w-0 border-y-[6px] border-y-transparent border-r-[6px] border-r-slate-900" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ProjectFilesModal: React.FC<ProjectFilesModalProps> = ({
  isOpen,
  onClose,
  workflowInstanceId,
  projectName,
  projectMemberIds,
}) => {
  const { t } = useTranslation(["projects", "common", "errors"]);
  const rootFolderLabel = t("projects:projectFilesModal.rootFolder");

  const [files, setFiles] = useState<GoogleDriveAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] =
    useState<GoogleDriveAttachment | null>(null);
  const [folderToDelete, setFolderToDelete] =
    useState<GoogleDriveAttachment | null>(null);
  const [downloadingFolderId, setDownloadingFolderId] = useState<string | null>(
    null
  );
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null
  );
  const [viewingFileId, setViewingFileId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<
    Array<{ id: string | null; name: string }>
  >([{ id: null, name: rootFolderLabel }]);

  const [sortField, setSortField] = useState<FileSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(
    INITIAL_COLUMN_FILTERS
  );

  const { user, isAdmin, isManager } = useAuth();

  const isProjectMember =
    typeof user?.id === "number" &&
    Array.isArray(projectMemberIds) &&
    projectMemberIds.includes(user.id);

  const canUploadFiles =
    isAdmin() ||
    isManager() ||
    (user?.role !== UserRole.VISITOR && isProjectMember);

  const canDeleteFiles = isAdmin() || isManager();

  const loadFiles = useCallback(async () => {
    if (!workflowInstanceId) return;

    try {
      setLoading(true);
      const filesData = await apiService.getWorkflowInstanceFiles(
        workflowInstanceId,
        currentFolderId
      );
      setFiles(filesData);
    } catch (error) {
      toast.error("Dosyalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [workflowInstanceId, currentFolderId]);

  useEffect(() => {
    if (isOpen && workflowInstanceId) {
      loadFiles();
      return;
    }

    setFiles([]);
    setCurrentFolderId(null);
    setFolderToDelete(null);
    setSortField(null);
    setSortOrder(null);
    setColumnFilters(INITIAL_COLUMN_FILTERS);
    setFolderPath([{ id: null, name: rootFolderLabel }]);
  }, [isOpen, workflowInstanceId, currentFolderId, loadFiles, rootFolderLabel]);

  const handleFolderClick = (file: GoogleDriveAttachment) => {
    if (!file || !file.id) return;
    setCurrentFolderId(file.id);
    setFolderPath((prev) => [...prev, { id: file.id, name: file.name }]);
  };

  const handleBreadcrumbClick = (index: number) => {
    const target = folderPath[index];
    setCurrentFolderId(target.id);
    setFolderPath(folderPath.slice(0, index + 1));
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return "-";
      }
      return date.toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const handleSort = (field: FileSortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortOrder("asc");
      return;
    }

    if (sortOrder === "asc") {
      setSortOrder("desc");
      return;
    }

    if (sortOrder === "desc") {
      setSortField(null);
      setSortOrder(null);
      return;
    }

    setSortOrder("asc");
  };

  const filteredAndSortedFiles = useMemo(() => {
    let result = [...files];

    result = result.filter((file) => {
      const nameValue = normalizeSearchText(file.name || "");
      const descriptionValue = normalizeSearchText(
        isFolder(file) ? "Klasör" : file.description || ""
      );
      const uploadedAtText = normalizeSearchText(formatDate(file.uploadedAt));
      const uploadedAtRaw = normalizeSearchText(file.uploadedAt || "");
      const uploadedByValue = normalizeSearchText(file.uploadedByName || "");
      const typeValue = normalizeSearchText(getTypeLabel(file));

      if (
        columnFilters.name.trim() &&
        !nameValue.includes(normalizeSearchText(columnFilters.name.trim()))
      ) {
        return false;
      }

      if (
        columnFilters.description.trim() &&
        !descriptionValue.includes(
          normalizeSearchText(columnFilters.description.trim())
        )
      ) {
        return false;
      }

      if (columnFilters.uploadedAt.trim()) {
        const query = normalizeSearchText(columnFilters.uploadedAt.trim());
        if (!uploadedAtText.includes(query) && !uploadedAtRaw.includes(query)) {
          return false;
        }
      }

      if (
        columnFilters.uploadedByName.trim() &&
        !uploadedByValue.includes(
          normalizeSearchText(columnFilters.uploadedByName.trim())
        )
      ) {
        return false;
      }

      if (
        columnFilters.type.trim() &&
        !typeValue.includes(normalizeSearchText(columnFilters.type.trim()))
      ) {
        return false;
      }

      return true;
    });

    if (!sortField || !sortOrder) {
      result.sort((a, b) => {
        const aIsFolder = isFolder(a);
        const bIsFolder = isFolder(b);
        if (aIsFolder !== bIsFolder) {
          return aIsFolder ? -1 : 1;
        }
        return (a.name || "").localeCompare(b.name || "", "tr");
      });
      return result;
    }

    result.sort((a, b) => {
      let cmp = 0;

      if (sortField === "uploadedAt") {
        const aTime = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
        const bTime = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
        cmp = aTime - bTime;
      } else {
        const aValue =
          sortField === "name"
            ? a.name || ""
            : sortField === "description"
            ? isFolder(a)
              ? "Klasör"
              : a.description || ""
            : sortField === "uploadedByName"
            ? a.uploadedByName || ""
            : getTypeLabel(a);

        const bValue =
          sortField === "name"
            ? b.name || ""
            : sortField === "description"
            ? isFolder(b)
              ? "Klasör"
              : b.description || ""
            : sortField === "uploadedByName"
            ? b.uploadedByName || ""
            : getTypeLabel(b);

        cmp = aValue.localeCompare(bValue, "tr", { sensitivity: "base" });
      }

      return sortOrder === "asc" ? cmp : -cmp;
    });

    return result;
  }, [files, columnFilters, sortField, sortOrder]);

  const handleDeleteFile = async (fileId: string) => {
    if (!workflowInstanceId) return;

    try {
      await apiService.deleteWorkflowInstanceFile(workflowInstanceId, fileId);
      setFiles((prev) => prev.filter((file) => file.id !== fileId));
      toast.success("Dosya silindi.");
    } catch (error) {
      toast.error("Dosya silinemedi.");
    }
  };

  const handleDeleteFolder = async (folder: GoogleDriveAttachment) => {
    if (!workflowInstanceId || !folder?.id) return;

    try {
      await apiService.deleteWorkflowInstanceFolder(workflowInstanceId, folder.id);
      setFiles((prev) => prev.filter((item) => item.id !== folder.id));
      toast.success(t("projects:projectFilesModal.folderDeleted"));
    } catch (error) {
      toast.error(t("projects:projectFilesModal.folderDeleteFailed"));
    }
  };

  const handleDownloadFolder = async (folder: GoogleDriveAttachment) => {
    if (!workflowInstanceId || !folder?.id) return;

    try {
      setDownloadingFolderId(folder.id);
      await apiService.downloadWorkflowInstanceFolder(
        workflowInstanceId,
        folder.id,
        `${folder.name || "folder"}.zip`
      );
    } catch (error) {
      toast.error(t("projects:projectFilesModal.folderDownloadFailed"));
    } finally {
      setDownloadingFolderId(null);
    }
  };

  const handleDownloadFile = async (file: GoogleDriveAttachment) => {
    if (!workflowInstanceId || !file?.id || isFolder(file)) return;

    try {
      setDownloadingFileId(file.id);
      await apiService.downloadWorkflowInstanceFile(
        workflowInstanceId,
        file.id,
        file.name || "download"
      );
    } catch (error) {
      toast.error("Dosya indirilemedi.");
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handlePreviewFile = async (file: GoogleDriveAttachment) => {
    if (!workflowInstanceId || !file?.id || isFolder(file)) return;

    const previewWindow = apiService.shouldOpenPreviewWindow(
      file.name,
      file.mimeType
    )
      ? window.open("", "_blank")
      : null;
    if (previewWindow) {
      previewWindow.opener = null;
      apiService.preparePreviewWindow(previewWindow, file.name);
    }

    try {
      setViewingFileId(file.id);
      await apiService.previewWorkflowInstanceFile(
        workflowInstanceId,
        file.id,
        file.name || "preview",
        previewWindow
      );
    } catch (error) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      toast.error("Dosya görüntülenemedi.");
    } finally {
      setViewingFileId(null);
    }
  };

  const SortIcon: React.FC<{ field: FileSortField }> = ({ field }) => {
    if (sortField !== field || !sortOrder) {
      return (
        <span className="inline-flex flex-col ml-1 text-slate-300">
          <ChevronUpIcon className="w-3 h-3 -mb-1" />
          <ChevronDownIcon className="w-3 h-3" />
        </span>
      );
    }

    return sortOrder === "asc" ? (
      <ChevronUpIcon className="w-3.5 h-3.5 ml-1 text-blue-600" />
    ) : (
      <ChevronDownIcon className="w-3.5 h-3.5 ml-1 text-blue-600" />
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[140] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <FolderIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {t("projects:projectFilesModal.title")}
              </h2>
              <p className="text-blue-100 text-sm">{projectName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
          {folderPath.length > 1 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-slate-600">
              {folderPath.map((crumb, index) => (
                <button
                  key={`${crumb.id || "root"}-${index}`}
                  type="button"
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`flex items-center gap-1 ${
                    index === folderPath.length - 1
                      ? "text-slate-900 font-semibold"
                      : "text-blue-600 hover:text-blue-700"
                  }`}
                >
                  <span>{crumb.name}</span>
                  {index < folderPath.length - 1 && (
                    <span className="text-slate-400">/</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : !workflowInstanceId ? (
            <div className="text-center py-12">
              <FolderIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">{t("projects:projectNotFound")}</p>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">
                {t("projects:projectFilesModal.empty")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-hidden">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-[26%] px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort("name")}
                        className="inline-flex items-center hover:text-blue-700"
                      >
                        {t("projects:projectFilesModal.file")}
                        <SortIcon field="name" />
                      </button>
                    </th>
                    <th className="w-[26%] px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort("description")}
                        className="inline-flex items-center hover:text-blue-700"
                      >
                        {t("projects:projectFilesModal.description")}
                        <SortIcon field="description" />
                      </button>
                    </th>
                    <th className="w-[18%] px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort("uploadedAt")}
                        className="inline-flex items-center hover:text-blue-700"
                      >
                        {t("projects:projectFilesModal.uploadedAt")}
                        <SortIcon field="uploadedAt" />
                      </button>
                    </th>
                    <th className="w-[18%] px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort("uploadedByName")}
                        className="inline-flex items-center hover:text-blue-700"
                      >
                        {t("projects:projectFilesModal.uploadedBy")}
                        <SortIcon field="uploadedByName" />
                      </button>
                    </th>
                    <th className="w-[18%] min-w-[250px] px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => handleSort("type")}
                        className="inline-flex items-center hover:text-blue-700"
                      >
                        {t("projects:projectFilesModal.type")}
                        <SortIcon field="type" />
                      </button>
                    </th>
                  </tr>
                  <tr className="bg-blue-50/40 border-t border-blue-100">
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={columnFilters.name}
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder={t("projects:projectFilesModal.search")}
                        className="w-full px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={columnFilters.description}
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        placeholder={t("projects:projectFilesModal.search")}
                        className="w-full px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={columnFilters.uploadedAt}
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            uploadedAt: e.target.value,
                          }))
                        }
                        placeholder={t("projects:projectFilesModal.searchDate")}
                        className="w-full px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={columnFilters.uploadedByName}
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            uploadedByName: e.target.value,
                          }))
                        }
                        placeholder={t("projects:projectFilesModal.search")}
                        className="w-full px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-2">
                      <input
                        type="text"
                        value={columnFilters.type}
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                        placeholder={t("projects:projectFilesModal.fileOrFolder")}
                        className="w-full px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedFiles.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-10 text-center text-sm text-gray-500"
                      >
                        {t("projects:projectFilesModal.noFilterResults")}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedFiles.map((file) => (
                      <tr key={file.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {isFolder(file) ? (
                            <FolderIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />
                          ) : file.iconUrl ? (
                            <img
                              src={file.iconUrl}
                              alt=""
                              className="w-6 h-6 flex-shrink-0"
                            />
                          ) : (
                            <DocumentTextIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
                          )}
                          <FileNameTypeCell file={file} />
                        </div>
                      </td>
                      <td className="px-4 py-3 relative">
                        {isFolder(file) ? (
                          <p className="text-sm text-slate-500">
                            {t("projects:projectFilesModal.folder")}
                          </p>
                        ) : file.description && file.description.length > 40 ? (
                          <div className="flex items-center gap-2 relative">
                            <div className="overflow-hidden flex-1">
                              <p className="text-sm text-gray-600 truncate">
                                {file.description}
                              </p>
                            </div>
                            <InformationCircleIcon className="peer w-4 h-4 text-blue-500 cursor-help flex-shrink-0" />
                            <div className="invisible peer-hover:visible absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 max-w-sm p-3 bg-blue-600 text-white text-sm rounded-lg shadow-2xl z-[999]">
                              <div className="break-words">{file.description}</div>
                              <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-blue-600"></div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">{file.description || "-"}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                        <p className="text-sm text-gray-600">{formatDate(file.uploadedAt)}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap overflow-hidden">
                        <p className="text-sm text-gray-600 truncate">{file.uploadedByName || "-"}</p>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap overflow-visible pr-4">
                        <div className="inline-flex items-center justify-end gap-2 flex-nowrap">
                          {isFolder(file) ? (
                            <>
                              <button
                              type="button"
                              onClick={() => handleFolderClick(file)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <span>{t("projects:projectFilesModal.open")}</span>
                              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleDownloadFolder(file)}
                              disabled={downloadingFolderId === file.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {downloadingFolderId === file.id ? (
                                <div className="w-3.5 h-3.5 border-2 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin" />
                              ) : (
                                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                              )}
                              <span>{t("projects:projectFilesModal.download")}</span>
                            </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void handlePreviewFile(file)}
                              disabled={viewingFileId === file.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {viewingFileId === file.id ? (
                                <div className="w-3.5 h-3.5 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                              ) : (
                                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                              )}
                              <span>{t("projects:projectFilesModal.open")}</span>
                            </button>
                          )}
                          {!isFolder(file) && (
                            <button
                              type="button"
                              onClick={() => void handleDownloadFile(file)}
                              disabled={downloadingFileId === file.id}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {downloadingFileId === file.id ? (
                                <div className="w-3.5 h-3.5 border-2 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin" />
                              ) : (
                                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                              )}
                              <span>{t("projects:projectFilesModal.download")}</span>
                            </button>
                          )}
                          {canDeleteFiles &&
                            (!isFolder(file) || !isProtectedFinanceFolder(file)) && (
                            <button
                              type="button"
                              onClick={() =>
                                isFolder(file)
                                  ? setFolderToDelete(file)
                                  : setFileToDelete(file)
                              }
                              className="inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title={
                                isFolder(file)
                                  ? t("projects:projectFilesModal.deleteFolder")
                                  : t("common:delete")
                              }
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                            )}
                          {canDeleteFiles &&
                            isFolder(file) &&
                            isProtectedFinanceFolder(file) && (
                            <span className="inline-flex h-8 w-8 flex-shrink-0" aria-hidden="true" />
                            )}
                        </div>
                      </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filteredAndSortedFiles.length === files.length
                ? t("projects:projectFilesModal.totalFiles", { count: files.length })
                : t("projects:projectFilesModal.filteredFiles", {
                    filtered: filteredAndSortedFiles.length,
                    total: files.length,
                  })}
            </p>
            <div className="flex items-center gap-3">
              {currentFolderId && folderPath.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    void handleDownloadFolder({
                      id: currentFolderId,
                      name: folderPath[folderPath.length - 1]?.name || "folder",
                      url: "",
                      mimeType: "application/vnd.google-apps.folder",
                      addedAt: new Date().toISOString(),
                    })
                  }
                  disabled={downloadingFolderId === currentFolderId}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {downloadingFolderId === currentFolderId ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  )}
                  {t("projects:projectFilesModal.downloadFolder")}
                </button>
              )}
              {canUploadFiles && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  disabled={!workflowInstanceId}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <CloudArrowUpIcon className="w-4 h-4" />
                  {t("projects:projectFilesModal.addFile")}
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                {t("common:close")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <UploadFilesToProjectModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          toast.success("Dosyalar başarıyla yüklendi!");
          loadFiles();
        }}
        userId={user?.id || 0}
        initialProjectId={workflowInstanceId ?? undefined}
        initialProjectName={projectName}
      />

      <ConfirmDialog
        isOpen={Boolean(fileToDelete)}
        onClose={() => setFileToDelete(null)}
        onConfirm={() => {
          if (fileToDelete) {
            void handleDeleteFile(fileToDelete.id);
          }
        }}
        title={t("common:delete")}
        message={t("projects:projectFilesModal.confirmDeleteFile")}
        confirmText={t("projects:projectFilesModal.confirmDelete")}
        cancelText={t("common:cancel")}
        type="danger"
      />

      <ConfirmDialog
        isOpen={Boolean(folderToDelete)}
        onClose={() => setFolderToDelete(null)}
        onConfirm={() => {
          if (folderToDelete) {
            void handleDeleteFolder(folderToDelete);
          }
        }}
        title={t("projects:projectFilesModal.deleteFolderTitle")}
        message={
          folderToDelete
            ? t("projects:projectFilesModal.deleteFolderMessage", {
                name: folderToDelete.name,
              })
            : ""
        }
        confirmText={t("projects:projectFilesModal.confirmDelete")}
        cancelText={t("common:cancel")}
        type="danger"
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

